import { debounce } from "@mui/material";

import { SEARCH_FIELD_LIMIT } from "@sinch/core/config/globals";
import { useLazyQueryHandler } from "@sinch/hooks/useLazyQueryHandler";
import { mapToOptions, Option } from "@sinch/utils/option";
import React, { FC, Ref, useMemo } from "react";
import { AutocompleteInput } from "../../../form/AutocompleteInput/AutocompleteInput";
import { AttendanceChip } from "../../entities/AttendanceChip";
import { clearPrefixForSearch, PrefixedEntities, SinchEntity, useSinchEntity } from "../../entities/entitiesProperties";
import { JobsData } from "./queries.graphql";
import { JobsDataQuery, JobsDataQueryVariables } from "./queries.types";
import { AutocompleteInputProps, AutocompleteOptions } from "@sinch/components/form/AutocompleteInput/types";

interface JobsSelectInputProps
  extends Pick<AutocompleteInputProps<AutocompleteOptions, true>, "slotProps" | "textFieldProps"> {
  /**
   * Input name
   */
  name: string;
  label: string;
  required?: boolean;
  value: (string | number)[] | undefined;
  onChange?: (val: (string | number)[] | null) => void;
  disabled?: boolean;
  error?: boolean;
  errorMessage: string[];
  inputRef: Ref<any>;
  onBlur: () => void;
  /**
   * Limit searched list of options
   */
  limit?: number;
}

export const JobsSelectInput: FC<JobsSelectInputProps> = ({ limit = SEARCH_FIELD_LIMIT, ...field }) => {
  const { getEntityIdLabel } = useSinchEntity();

  const { data, dispatch, loading } = useLazyQueryHandler<JobsDataQuery, JobsDataQueryVariables>(JobsData);
  const options: Option[] = useMemo(
    () => mapToOptions("id", ({ name, id }) => `${getEntityIdLabel(SinchEntity.Job, id)} ${name}`)(data?.jobs || []),
    [data?.jobs]
  );

  const debounceDispatch = useMemo(() => debounce(dispatch, 200), [dispatch]);

  const handleSearch = (search: string) => {
    debounceDispatch({ limit, filter: { search: { LIKE: clearPrefixForSearch(search, PrefixedEntities.Job) } } });
  };

  return (
    <AutocompleteInput<AutocompleteOptions, true>
      size="small"
      {...field}
      limitCount={limit}
      loading={loading}
      multiple
      onSearch={handleSearch}
      openOnFocus
      options={options}
      renderTags={(vals, getTagProps) =>
        vals.map((val, index) => (
          <AttendanceChip key={val.value} getTagProps={getTagProps} index={index} val={val} variant={SinchEntity.Job} />
        ))
      }
    />
  );
};
